import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Mail, MessageSquare, MessagesSquare } from 'lucide-react';

/**
 * Phase 2 — Read-only communication log surfaces.
 * - Email Log:        GET /communications/email?recipientType=PARTICIPANT&recipientId={id}
 * - SMS Log:          GET /communications/sms?recipientType=PARTICIPANT&recipientId={id}
 * - Engagement Log:   GET /engagement/messages?participantId={id}
 *
 * NO send actions in this phase. Phase 3 may add composers (subject to user approval).
 */

function StatusPill({ status }) {
  const s = (status || '').toUpperCase();
  const cls =
    s === 'SENT' || s === 'DELIVERED' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
    s === 'FAILED' ? 'bg-red-100 text-red-700 border-red-300' :
    s === 'QUEUED' ? 'bg-blue-100 text-blue-700 border-blue-300' :
    'bg-muted text-muted-foreground';
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{s || 'UNKNOWN'}</Badge>;
}

function LogTable({ loading, items, columns, emptyMsg, testidPrefix }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(c => <TableHead key={c.key}>{c.label}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableRow><TableCell colSpan={columns.length} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
        ) : items.length === 0 ? (
          <TableRow><TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">{emptyMsg}</TableCell></TableRow>
        ) : items.map((it, idx) => (
          <TableRow key={it.id || it._id || idx} data-testid={`${testidPrefix}-row-${idx}`}>
            {columns.map(c => (
              <TableCell key={c.key} className={c.cellClass || ''}>
                {c.render ? c.render(it) : (it[c.key] ?? '—')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function EmailLogTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/communications/email', { params: { recipientType: 'PARTICIPANT', recipientId: participantId, pageSize: 50 } })
      .then(r => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const columns = [
    { key: 'createdAt', label: 'Sent', cellClass: 'text-xs font-mono', render: (it) => (it.createdAt || '').split('T')[0] || '—' },
    { key: 'subject', label: 'Subject', render: (it) => <span className="font-medium">{it.subject || '(no subject)'}</span> },
    { key: 'toAddress', label: 'To', cellClass: 'text-xs', render: (it) => it.toAddress || it.to || '—' },
    { key: 'status', label: 'Status', render: (it) => <StatusPill status={it.status} /> },
  ];

  return (
    <Card data-testid="email-log-tab">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Email Log</CardTitle>
        <p className="text-xs text-muted-foreground">Read-only. Showing the most recent 50 emails sent to or about this participant.</p>
      </CardHeader>
      <CardContent className="p-0">
        <LogTable loading={loading} items={items} columns={columns} emptyMsg="No emails recorded for this participant." testidPrefix="email" />
      </CardContent>
    </Card>
  );
}

export function SmsLogTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/communications/sms', { params: { recipientType: 'PARTICIPANT', recipientId: participantId, pageSize: 50 } })
      .then(r => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const columns = [
    { key: 'createdAt', label: 'Sent', cellClass: 'text-xs font-mono', render: (it) => (it.createdAt || '').split('T')[0] || '—' },
    { key: 'toPhone', label: 'To', cellClass: 'text-xs', render: (it) => it.toPhone || it.to || '—' },
    { key: 'body', label: 'Message', render: (it) => <span className="text-sm">{(it.body || it.message || '').slice(0, 100)}</span> },
    { key: 'status', label: 'Status', render: (it) => <StatusPill status={it.status} /> },
  ];

  return (
    <Card data-testid="sms-log-tab">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> SMS Log</CardTitle>
        <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
          <span>Read-only. Twilio send flows are</span>
          <Badge variant="outline" className="text-[10px]">MOCKED</Badge>
          <span>until credentials are provided.</span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <LogTable loading={loading} items={items} columns={columns} emptyMsg="No SMS messages recorded for this participant." testidPrefix="sms" />
      </CardContent>
    </Card>
  );
}

export function EngagementThreadsTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/engagement/messages', { params: { participantId, pageSize: 50 } })
      .then(r => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const columns = [
    { key: 'createdAt', label: 'Sent', cellClass: 'text-xs font-mono', render: (it) => (it.createdAt || '').split('T')[0] || '—' },
    { key: 'direction', label: 'Direction', render: (it) => <Badge variant="outline" className="text-[10px]">{it.direction || '—'}</Badge> },
    { key: 'messageType', label: 'Channel', render: (it) => <Badge variant="outline" className="text-[10px]">{it.messageType || '—'}</Badge> },
    { key: 'contactName', label: 'Contact', cellClass: 'text-sm', render: (it) => it.contactName || it.contactId || '—' },
    { key: 'body', label: 'Message', render: (it) => <span className="text-sm">{(it.body || it.message || '').slice(0, 120)}</span> },
    { key: 'sentByName', label: 'By', cellClass: 'text-xs', render: (it) => it.sentByName || '—' },
  ];

  return (
    <Card data-testid="engagement-threads-tab">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><MessagesSquare className="h-4 w-4" /> Engagement Threads</CardTitle>
        <p className="text-xs text-muted-foreground">Read-only. Messages exchanged with this participant's contacts through the Engagement module.</p>
      </CardHeader>
      <CardContent className="p-0">
        <LogTable loading={loading} items={items} columns={columns} emptyMsg="No engagement messages for this participant." testidPrefix="engagement" />
      </CardContent>
    </Card>
  );
}
